import { useContext, useEffect, useMemo, useState } from "react";
import { PermissionContext } from "../../contexts/PermissionContexts";
import { EmployeeWarningsList } from "../../components/WarningComponents";
import { FaInfo, FaInfoCircle, FaSearch } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Colors,
} from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
import {
  requestGetDailyUserStats,
  requestUserDailySummary,
} from "../../requests/stats_requests";
import { useNavigate, useParams } from "react-router-dom";
import { requestDev } from "../../requests/users_requests";
import { ActionStatus, BACKEND_URI } from "../../entries";
import { AvatarBubble } from "../../components/DeveloperComponents";

ChartJS.register(ArcElement, Tooltip, Legend);

const centerTextPlugin = {
  id: "centerText",
  beforeDraw: (chart) => {
    if (chart.config.type === "line") return;
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;

    const centerX = meta.data[0].x;
    const centerY = meta.data[0].y;
    const isDark = document.documentElement.classList.contains("dark");
    ctx.save();
    const total = chart.config.options.plugins.centerText?.value || 0;
    ctx.fillStyle = isDark ? "#ffffff" : "#111827";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "900 36px sans-serif";
    ctx.fillText(total.toString(), centerX, centerY - 12);
    ctx.font = "bold 12px sans-serif";
    ctx.fillText("TOPLAM", centerX, centerY + 18);
    ctx.restore();
  },
};
ChartJS.register(centerTextPlugin);
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
);
const COLORS = {
  ASSIGNED: "#0EA5E9",
  RESOLVED: "#00ff08",
  CLOSED: "#9e0310",
  REOPENED: "#F59E0B",
  ON_HOLD: "#8B5CF6",
  UPDATED: "#ffdd00",
  UNBANNED: "#243eff",
  BANNED: "#e3525e",
  REPLIED: "#3b82f6",
  "WAITING DEV": "#f97316",
  OTHER: "#64748B",
};

export const UserDetail = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalTicketCount, setTotalTicketCount] = useState(0);
  const [totalResolvedCount, setTotalResolvedCount] = useState(0);
  const [totalClosedCount, setTotalClosedCount] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [activeTab, setActiveTab] = useState("tickets");
  const [dateRangeType, setDateRangeType] = useState("today");
  const [customStartDate, setCustomStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [customEndDate, setCustomEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [actionFilter, setActionFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dev, setDev] = useState(null);
  const [totalLogs, setTotalLogs] = useState(0);
  const [hourlyData, setHourlyData] = useState({});
  const [selectedHourLogs, setSelectedHourLogs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [highlightedTicketId, setHighlightedTicketId] = useState(null);
  const [chartLogs, setChartLogs] = useState([]);
  const [highlightConfig, setHighlightConfig] = useState({
    type: null,
    value: null,
  });

  const pageSize = 20;
  const { userId } = useParams();
  const navigator = useNavigate();
  const { hasPermission } = useContext(PermissionContext);

  const getDateRange = () => {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split("T")[0];

    let startDate = formatDate(today);
    let endDate = formatDate(today);

    switch (dateRangeType) {
      case "today":
        break;
      case "yesterday":
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = formatDate(yesterday);
        endDate = formatDate(yesterday);
        break;
      case "week":
        const lastWeek = new Date();
        lastWeek.setDate(lastWeek.getDate() - 7);
        startDate = formatDate(lastWeek);
        break;
      case "month":
        const lastMonth = new Date();
        lastMonth.setDate(lastMonth.getDate() - 30);
        startDate = formatDate(lastMonth);
        break;
      case "custom":
        startDate = customStartDate;
        endDate = customEndDate;
        break;
      default:
        break;
    }

    return { startDate, endDate };
  };

  const getDisplayDateText = () => {
    switch (dateRangeType) {
      case "today":
        return "Bugün";
      case "yesterday":
        return "Dün";
      case "week":
        return "Son 1 Hafta";
      case "month":
        return "Son 1 Ay";
      case "custom":
        return `${customStartDate} / ${customEndDate}`;
      default:
        return "";
    }
  };

  const fetchData = async () => {
    if (!userId) return;
    const { startDate, endDate } = getDateRange();

    try {
      requestUserDailySummary({
        startDate,
        endDate,
        userId,
        page,
        pageSize,
      }).then((res) => {
        setSummary(res.summary);
        setTotalTicketCount(res.total_tickets_count);
        setTotalResolvedCount(res.total_resolved_tickets_count);
        setTotalClosedCount(res.total_closed_tickets_count);
        setTickets(res.total_tickets || []);
        setTotalPages(res.total_pages || 1);
      });

      requestGetDailyUserStats({
        startDate,
        endDate,
        userId,
        page,
        pageSize,
        action: actionFilter,
      }).then((res) => {
        setLogs(res.logs);
        setTotalPages(res.total_pages || 1);
        setTotalLogs(res.total || 0);
      });
    } catch (error) {
      console.error("Veri çekilirken hata oluştu:", error);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [dateRangeType, customStartDate, customEndDate, actionFilter]);

  useEffect(() => {
    fetchData();
  }, [
    dateRangeType,
    customStartDate,
    customEndDate,
    actionFilter,
    page,
    userId,
    activeTab,
  ]);

  useEffect(() => {
    requestDev({ userId }).then((res) => {
      setDev(res.dev);
    });
  }, [userId]);

  const filteredLogs = useMemo(() => {
    if (!searchTerm) return logs;

    const searchLower = searchTerm.toLowerCase();
    return logs.filter(
      (log) =>
        log.ticket_id.toLowerCase().includes(searchLower) ||
        log.action.toLowerCase().includes(searchLower),
    );
  }, [logs, searchTerm]);

  const filteredTickets = tickets.filter((ticket) => {
    const matchStatus = !actionFilter || ticket.status === actionFilter;

    if (!searchTerm) return matchStatus;
    const searchLower = searchTerm.toLowerCase();
    const matchSearch =
      ticket.id?.toLowerCase().includes(searchLower) ||
      ticket.status?.toLowerCase().includes(searchLower) ||
      ticket.subject?.toLowerCase().includes(searchLower) ||
      ticket.characterName?.toLowerCase().includes(searchLower);

    return matchStatus && matchSearch;
  });

  const rawChartData = summary
    ? [
        {
          name: "Aktif",
          value:
            totalTicketCount - (totalResolvedCount + totalClosedCount) || 0,
          color: COLORS.ASSIGNED,
        },
        {
          name: "Çözülen",
          value: totalResolvedCount || 0,
          color: COLORS.RESOLVED,
        },
        {
          name: "Kapatılan",
          value: totalClosedCount || 0,
          color: COLORS.CLOSED,
        },
      ].filter((item) => item.value > 0)
    : [];

  const chartJsData = {
    labels: rawChartData.map((d) => d.name),
    datasets: [
      {
        data: rawChartData.map((d) => d.value),
        backgroundColor: rawChartData.map((d) => d.color),
        borderWidth: 0,
        hoverOffset: 12,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "75%",
    layout: { padding: 12 },
    plugins: {
      centerText: { value: totalTicketCount || 0 },
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, padding: 20 },
      },
      tooltip: {
        backgroundColor: "rgba(17, 24, 39, 0.9)",
        padding: 12,
        cornerRadius: 8,
        titleFont: { size: 13 },
        bodyFont: { size: 13, weight: "bold" },
      },
    },
  };

  const gradientColors = {
    unbanned: "from-[#243eff]",
    banned: "to-[#9e0310]",
  };
  const processHourlyData = (allLogs) => {
    const groups = {};
    for (let i = 10; i <= 19; i++) {
      groups[`${i}:00`] = [];
    }

    allLogs.forEach((log) => {
      const hour = new Date(log.timestamp * 1000).getHours();
      if (hour >= 10 && hour <= 19) {
        groups[`${hour}:00`].push(log);
      }
    });
    setHourlyData(groups);
  };

  const lineChartData = {
    labels: Object.keys(hourlyData),
    datasets: [
      {
        label: "Tüm İşlemler",
        data: Object.values(hourlyData).map((l) => l.length),
        borderColor: highlightConfig.value ? "rgba(59,130,246,0.3)" : "#006fc9",
        backgroundColor: "transparent",
        tension: 0.4,
        pointRadius: 2,
      },
      ...(highlightConfig.value
        ? [
            {
              label:
                highlightConfig.type === "TICKET"
                  ? `Ticket: ${highlightConfig.value}`
                  : `İşlem: ${ActionStatus[highlightConfig.value]?.title}`,
              data: Object.keys(hourlyData).map((hour) => {
                const hourLogs = hourlyData[hour];
                return hourLogs.filter((l) => {
                  if (highlightConfig.type === "TICKET")
                    return l.ticket_id === highlightConfig.value;
                  if (highlightConfig.type === "ACTION")
                    return l.action === highlightConfig.value;
                  return false;
                }).length;
              }),
              borderColor:
                highlightConfig.type === "TICKET" ? "#22c55e" : "#f59e0b",
              backgroundColor:
                highlightConfig.type === "TICKET"
                  ? "rgba(34,197,94,0.2)"
                  : "rgba(245,158,11,0.2)",
              pointRadius: 6,
              pointHoverRadius: 8,
              borderWidth: 3,
              tension: 0.4,
              fill: true,
            },
          ]
        : []),
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    layout: {
      padding: {
        left: 10,
        right: 20,
        top: 20,
        bottom: 0,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grace: "5%",
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    onClick: (_, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const hourLabel = Object.keys(hourlyData)[index];
        setSelectedHourLogs(hourlyData[hourLabel]);
        setIsModalOpen(true);
      }
    },
  };

  useEffect(() => {
    if (!userId) return;
    const { startDate, endDate } = getDateRange();
    requestGetDailyUserStats({
      startDate,
      endDate,
      userId,
      page: 1,
      pageSize: 100000,
    }).then((res) => {
      setChartLogs(res.logs || []);
    });
  }, [dateRangeType, customStartDate, customEndDate, userId]);

  useEffect(() => {
    processHourlyData(chartLogs);
  }, [chartLogs]);

  return (
    <div className="p-4 sm:p-6 min-h-screen space-y-6 w-full relative dark:text-base-content/70">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 p-5 sm:p-6 rounded-2xl shadow-sm border dark:border-base-300">
        <div>
          <div className="flex gap-3 mb-4">
            {dev && <AvatarBubble dev={dev} />}
            <div>
              <h1 className="text-2xl font-bold dark:text-white">
                {dev?.name}
              </h1>
              <p className="text-sm dark:text-white mt-1">
                Seçili Dönem:{" "}
                <span className="font-semibold text-primary dark:text-blue-500">
                  {getDisplayDateText()}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {dateRangeType === "custom" && (
            <div className="flex items-center gap-2 mr-2">
              <input
                type="date"
                className="input input-bordered input-sm bg-gray-200 dark:bg-base-200"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
              <span>-</span>
              <input
                type="date"
                className="input input-bordered input-sm bg-gray-200 dark:bg-base-200"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </div>
          )}
          <select
            className="select select-bordered select-sm w-36 bg-white dark:bg-base-200 dark:text-base-content"
            value={dateRangeType}
            onChange={(e) => setDateRangeType(e.target.value)}
          >
            <option value="today">Bugün</option>
            <option value="yesterday">Dün</option>
            <option value="week">Son 1 Hafta</option>
            <option value="month">Son 1 Ay</option>
            <option value="custom">Özel Tarih</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="col-span-1 p-6 rounded-2xl shadow-sm border dark:border-base-300 flex flex-col min-h-[350px]">
          <h2
            className="text-lg font-bold mb-4 flex items-center gap-2"
            title="Günlük Talep İstatistiklerini Gösterir"
          >
            Talep Dağılımı <FaInfoCircle />
          </h2>
          {rawChartData.length > 0 ? (
            <div className="flex-1 w-full h-full min-h-[250px] relative flex flex-col items-center justify-center">
              <div className="w-full h-full absolute inset-0 text-red-500">
                <Doughnut data={chartJsData} options={chartOptions} />
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm ">
              Bu tarih aralığında özet veri bulunamadı.
            </div>
          )}
          <div className="flex gap-2 items-center">
            <div className="w-2 h-2 rounded-sm bg-info"></div> Aktif Talep:{" "}
            {totalTicketCount - (totalResolvedCount + totalClosedCount) || 0}
          </div>
          <div className="flex gap-2 items-center">
            <div className="w-2 h-2 rounded-sm bg-success"></div> Çözülen Talep:{" "}
            {totalResolvedCount || 0}
          </div>
          <div className="flex gap-2 items-center">
            <div className="w-2 h-2 rounded-sm bg-red-800"></div> Kapatılan
            Talep: {totalClosedCount || 0}
          </div>
        </div>

        <div className="col-span-1 lg:col-span-2 p-6 rounded-2xl shadow-sm border border-base-300 flex flex-col">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h2
              className="text-lg font-bold whitespace-nowrap text-base-content flex items-center gap-2"
              title="Günlük Yapılan İşlemlerin Sayısını Gösterir"
            >
              İşlem Özetleri <FaInfoCircle />
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 w-full">
            {ActionStatus &&
              Object.entries(ActionStatus)
                .filter(
                  ([_, status]) =>
                    status.inBox &&
                    status.key != "BANNED" &&
                    status.key != "UNBANNED",
                )
                .map(([key, status]) => {
                  const count = summary ? summary[key] || 0 : 0;
                  return (
                    <div
                      key={key}
                      className="relative flex flex-col p-4 rounded-xl border border-gray-500 dark:border-gray-700 dark:bg-base-200/50 dark:hover:bg-base-200 transition-all duration-300 group hover:-translate-y-1 hover:shadow-md"
                    >
                      <div
                        className="absolute top-0 left-0 w-full h-1 rounded-t-xl opacity-70 group-hover:opacity-100 transition-opacity"
                        style={{ backgroundColor: COLORS[key] || COLORS.OTHER }}
                      ></div>
                      <div className="flex justify-between items-center mb-2 mt-1">
                        <span className="text-[11px] font-bold dark:text-base-content/60 uppercase tracking-widest truncate pr-2">
                          {status.title}
                        </span>
                        <span
                          className="text-md p-0.5 rounded-full flex-shrink-0 shadow-sm text-white"
                          style={{
                            backgroundColor: COLORS[key] || COLORS.OTHER,
                            boxShadow: `0 0 8px ${COLORS[key] || COLORS.OTHER}80`,
                          }}
                          title={status.description}
                        >
                          <FaInfo />
                        </span>
                      </div>
                      <div className="text-3xl font-black dark:text-base-content tabular-nums">
                        {count}
                      </div>
                    </div>
                  );
                })}
            <div
              key={ActionStatus.BANNED.key}
              className="relative flex flex-col p-4 rounded-xl border border-gray-500 dark:border-gray-700 dark:bg-base-200/50 dark:hover:bg-base-200 transition-all duration-300 group hover:-translate-y-1 hover:shadow-md"
            >
              <div
                className={`absolute top-0 left-0 w-full h-1 rounded-t-xl opacity-70 group-hover:opacity-100 transition-opacity bg-gradient-to-l ${gradientColors.unbanned} ${gradientColors.banned}`}
              ></div>
              <div className="flex justify-between items-center mb-2 mt-1">
                <span className="text-[11px] font-bold dark:text-base-content/60 uppercase tracking-widest truncate pr-2">
                  {"Yasaklama / Yasak Kaldırma"}
                </span>
                <span
                  className={`text-md p-0.5 rounded-full flex-shrink-0 shadow-sm text-white bg-gradient-to-tl ${gradientColors.unbanned} ${gradientColors.banned}`}
                  title={
                    ActionStatus.BANNED.description +
                    " / " +
                    ActionStatus.UNBANNED.description
                  }
                >
                  <FaInfo />
                </span>
              </div>
              <div className="text-3xl font-black dark:text-base-content tabular-nums justify-between gap-5">
                <span>{summary?.BANNED || 0}</span> /{" "}
                <span>{summary?.UNBANNED || 0}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-1 lg:col-span-3 p-6 rounded-2xl shadow-sm border dark:border-base-300 flex flex-col">
          <h2
            className="text-lg font-bold whitespace-nowrap text-base-content flex items-center mb-2 gap-2"
            title={
              activeTab === "tickets"
                ? "Günlük İşlem Yapılan Talepleri Listeler"
                : "Günlük İşlem Kayıtlarını Listeler"
            }
          >
            {activeTab === "tickets" ? "Talepler" : "İşlem Kayıtları"}{" "}
            <FaInfoCircle />
          </h2>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
            <div className="tabs tabs-boxed bg-base-200/50 p-1">
              <a
                className={`tab ${activeTab === "tickets" ? "bg-primary shadow-sm font-bold text-white dark:text-base-content" : "text-white dark:text-base-content"}`}
                onClick={() => setActiveTab("tickets")}
              >
                Talepler ({totalTicketCount})
              </a>
              <a
                className={`tab ${activeTab === "logs" ? "bg-primary shadow-sm font-bold text-white dark:text-base-content" : "text-white dark:text-base-content"}`}
                onClick={() => setActiveTab("logs")}
              >
                İşlem Kayıtları ({totalLogs})
              </a>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
              <div className="relative flex w-full sm:w-64 border dark:border-base-300 rounded-lg focus-within:border-primary">
                <FaSearch className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 font-thin text-base-content/70" />
                <input
                  type="text"
                  placeholder="Ara..."
                  className="input input-sm w-full pl-9 focus:outline-none focus:border-0 bg-transparent dark:text-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <MdClose
                  className={`self-center mx-2 hover:scale-110 cursor-pointer hover:text-red-500 ${searchTerm ? "block" : "hidden"}`}
                  onClick={() => setSearchTerm("")}
                />
              </div>
              <select
                className="select select-bordered select-sm w-full sm:w-36 bg-white dark:bg-base-200 dark:text-base-content"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
              >
                <option value="">Tümü</option>
                {Object.entries(ActionStatus).map(([key, status]) => (
                  <option key={key} value={key}>
                    {status.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto flex-1 mt-2">
            <table className="table table-sm w-full">
              <thead>
                <tr className="text-black dark:text-base-content border-b dark:border-gray-700">
                  <th className="rounded-l-lg">Tarih / Saat</th>
                  <th>Ticket ID</th>
                  {activeTab === "tickets" && <th>Konu / Karakter</th>}
                  <th className="rounded-r-lg">
                    {activeTab === "logs" ? "Yapılan İşlem" : "Durum"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {activeTab === "logs" &&
                  (filteredLogs.length > 0 ? (
                    filteredLogs.map((log, index) => {
                      const logDate = new Date(log.timestamp * 1000);
                      const dateString = logDate.toLocaleDateString("tr-TR");
                      const timeString = logDate.toLocaleTimeString("tr-TR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      });

                      return (
                        <tr
                          key={`log-${index}`}
                          className="hover:bg-gray-200 dark:hover:bg-gray-600 border-b dark:border-base-200 last:border-0 transition-colors cursor-pointer"
                          onMouseUp={(e) => {
                            if (e.button === 2) return;
                            const ticketUrl = `/admin/${log.fetch_code}/edit`;
                            if (e.button === 1 || e.ctrlKey || e.metaKey)
                              window.open(ticketUrl, "_blank");
                            else navigator(ticketUrl);
                          }}
                        >
                          <td className="whitespace-nowrap">
                            <span className="font-medium">{dateString}</span>
                            <span className="text-xs ml-2 opacity-70">
                              {timeString}
                            </span>
                          </td>
                          <td className="font-mono text-xs">{log.ticket_id}</td>
                          <td>
                            <div
                              className="badge badge-sm border-none text-white font-medium px-2 py-3"
                              style={{
                                backgroundColor:
                                  COLORS[log.action] || COLORS.OTHER,
                              }}
                              title={
                                ActionStatus[log.action]?.description ||
                                log.action
                              }
                            >
                              {ActionStatus[log.action]?.title || log.action}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center py-12">
                        Log kaydı bulunamadı.
                      </td>
                    </tr>
                  ))}

                {activeTab === "tickets" &&
                  (filteredTickets.length > 0 ? (
                    filteredTickets.map((ticket, index) => {
                      const ticketDate = new Date(ticket.createdAt * 1000);
                      const dateString = ticketDate.toLocaleDateString("tr-TR");
                      const timeString = ticketDate.toLocaleTimeString(
                        "tr-TR",
                        { hour: "2-digit", minute: "2-digit" },
                      );

                      return (
                        <tr
                          key={`ticket-${ticket.id || index}`}
                          className="hover:bg-gray-200 dark:hover:bg-gray-600 border-b dark:border-base-200 last:border-0 transition-colors cursor-pointer"
                          onMouseUp={(e) => {
                            if (e.button === 2) return;
                            const ticketUrl = `/admin/${ticket.fetch_code}/edit`;
                            if (e.button === 1 || e.ctrlKey || e.metaKey)
                              window.open(ticketUrl, "_blank");
                            else navigator(ticketUrl);
                          }}
                        >
                          <td className="whitespace-nowrap">
                            <span className="font-medium">{dateString}</span>
                            <span className="text-xs ml-2 opacity-70">
                              {timeString}
                            </span>
                          </td>
                          <td className="font-mono text-xs">{ticket.id}</td>
                          <td className="max-w-xs truncate">
                            <div className="font-semibold">
                              {ticket.subject}
                            </div>
                            <div className="text-xs opacity-70">
                              {ticket.characterName}
                            </div>
                          </td>
                          <td>
                            <div
                              className="badge badge-sm border-none text-white font-medium px-2 py-3"
                              style={{
                                backgroundColor:
                                  COLORS[ticket.status] || COLORS.OTHER,
                              }}
                              title={
                                ActionStatus[ticket.status.replace(" ", "_")]
                                  ?.description || ticket.status
                              }
                            >
                              {ActionStatus[ticket.status.replace(" ", "_")]
                                ?.title || ticket.status}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-12">
                        Kriterlere uygun talep bulunamadı.
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6 pt-4 border-t border-base-300">
              <span className="text-sm">
                Sayfa {page} / {totalPages}
              </span>
              <div className="join">
                <button
                  className="join-item btn btn-sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  « Önceki
                </button>
                <button
                  className="join-item btn btn-sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Sonraki »
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="col-span-1 lg:col-span-3 p-4 sm:p-6 rounded-2xl border dark:border-base-300 bg-base-100 flex flex-col">
          <h2 className="text-lg font-bold mb-4 px-2">
            İşlem Grafiği (10:00 - 19:00)
          </h2>

          <div className="flex-1 w-full min-h-[400px]">
            <Line data={lineChartData} options={lineOptions} />
          </div>
        </div>

        {isModalOpen && (
          <div className="modal modal-open">
            <div className="modal-box w-11/12 max-w-3xl">
              <h3 className="font-bold text-lg border-b pb-2 mb-4">
                Saat Detayı:{" "}
                {selectedHourLogs.length > 0 &&
                  new Date(selectedHourLogs[0].timestamp * 1000).getHours()}
                :00
              </h3>
              <div className="overflow-y-auto max-h-96">
                <table className="table table-compact w-full">
                  <thead>
                    <tr>
                      <th>Ticket ID</th>
                      <th>İşlem</th>
                      <th>Aksiyon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedHourLogs.length === 0 && (
                      <tr>
                        <td colSpan="3" className="text-center py-12">
                          Kayıt bulunamadı.
                        </td>
                      </tr>
                    )}
                    {selectedHourLogs.map((log, i) => (
                      <tr key={i} className="hover:bg-base-200">
                        <td
                          className="font-mono text-blue-500 underline cursor-pointer"
                          onMouseUp={(e) => {
                            if (e.button === 2) return;
                            const ticketUrl = `/admin/${log.fetch_code}/edit`;
                            if (e.button === 1 || e.ctrlKey || e.metaKey)
                              window.open(ticketUrl, "_blank");
                            else navigator(ticketUrl);
                          }}
                        >
                          {log.ticket_id}
                        </td>
                        <td
                          className="cursor-pointer hover:text-primary font-semibold gap-2 flex items-center"
                          title="Bu işlem tipindeki tüm hareketleri vurgula"
                        >
                          <span
                            style={{
                              backgroundColor: COLORS[log.action],
                              borderColor: COLORS[log.action],
                            }}
                            className="p-1.5  w-max rounded-full badge badge-outline text-white"
                          >
                            {ActionStatus[log.action]?.title}
                          </span>
                          <button
                            className="btn btn-xs btn-outline"
                            style={{
                              borderColor: COLORS[log.action],
                              color: COLORS[log.action],
                            }}
                            onClick={() => {
                              setHighlightConfig({
                                type: "ACTION",
                                value: log.action,
                              });
                              setIsModalOpen(false);
                            }}
                          >
                            İşlemi Vurgula
                          </button>
                        </td>
                        <td>
                          <button
                            className="btn btn-xs btn-success btn-outline"
                            onClick={() => {
                              setHighlightConfig({
                                type: "TICKET",
                                value: log.ticket_id,
                              });
                              setIsModalOpen(false);
                              setTimeout(
                                () =>
                                  window.scrollTo({
                                    top: document.body.scrollHeight,
                                    behavior: "smooth",
                                  }),
                                100,
                              );
                            }}
                          >
                            Ticket'ı Vurgula
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="modal-action">
                <button
                  className="btn"
                  onClick={() => {
                    setIsModalOpen(false);
                    setHighlightedTicketId(null);
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {hasPermission("warnings:view") && (
        <div className="p-5 sm:p-6 rounded-2xl shadow-sm border dark:border-base-300">
          <EmployeeWarningsList userId={userId} />
        </div>
      )}
    </div>
  );
};
